import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Upload,
  BookOpen,
  Play,
  Edit,
  Save,
  X,
  Loader2,
  Image as ImageIcon,
  GraduationCap,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
  Check,
  AlertCircle,
  Tag,
  Percent,
  Timer,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = 'abba_admin_2026'; // Default admin key

export default function AdminStorePage() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState(ADMIN_KEY);
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  
  // New topic form
  const [showNewTopicForm, setShowNewTopicForm] = useState(false);
  const [newTopic, setNewTopic] = useState({
    topic_name: '',
    grade: 'Grade 9',
    tier: 'Higher',
    description: ''
  });
  
  // Expanded topics
  const [expandedTopics, setExpandedTopics] = useState({});
  
  // Upload states
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  
  // Message state
  const [message, setMessage] = useState(null);

  useEffect(() => {
    verifyAndFetch();
  }, []);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const verifyAndFetch = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/topics?api_key=${apiKey}`);
      if (response.ok) {
        setAuthenticated(true);
        const data = await response.json();
        setTopics(data.topics);
      } else {
        setAuthenticated(false);
        setShowApiKeyInput(true);
      }
    } catch (err) {
      showMessage('Failed to connect to server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    await verifyAndFetch();
  };

  const createTopic = async () => {
    if (!newTopic.topic_name.trim()) {
      showMessage('Topic name is required', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/topics?api_key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTopic)
      });
      
      if (response.ok) {
        const data = await response.json();
        setTopics([...topics, data.topic]);
        setNewTopic({ topic_name: '', grade: 'Grade 9', tier: 'Higher', description: '' });
        setShowNewTopicForm(false);
        setExpandedTopics({ ...expandedTopics, [data.topic.topic_id]: true });
        showMessage('Topic created successfully!');
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to create topic', 'error');
      }
    } catch (err) {
      showMessage('Failed to create topic', 'error');
    }
  };

  const updateTopic = async (topicId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/topics/${topicId}?api_key=${apiKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        const data = await response.json();
        setTopics(topics.map(t => t.topic_id === topicId ? data.topic : t));
        showMessage('Topic updated');
      }
    } catch (err) {
      showMessage('Failed to update topic', 'error');
    }
  };

  const setFlashSale = async (topicId, saleData) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/topics/${topicId}/flash-sale?api_key=${apiKey}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(saleData)
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTopics(topics.map(t => t.topic_id === topicId ? data.topic : t));
        showMessage(saleData.enabled ? 'Flash sale enabled!' : 'Flash sale disabled');
      }
    } catch (err) {
      showMessage('Failed to update flash sale', 'error');
    }
  };

  const uploadCardImage = async (topicId, file) => {
    setUploading({ ...uploading, [topicId]: true });
    
    try {
      // Get Cloudinary signature
      const sigResponse = await fetch(`${API_URL}/api/dashboard/cloudinary-signature`);
      const sigData = await sigResponse.json();
      
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sigData.api_key);
      formData.append('timestamp', sigData.timestamp);
      formData.append('signature', sigData.signature);
      formData.append('folder', 'abba_academy/store_cards');
      
      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      if (!uploadResponse.ok) throw new Error('Upload failed');
      
      const uploadData = await uploadResponse.json();
      
      // Add card to topic
      const cardResponse = await fetch(
        `${API_URL}/api/admin/topics/${topicId}/cards?api_key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            url: uploadData.secure_url,
            title: file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ')
          })
        }
      );
      
      if (cardResponse.ok) {
        // Refresh topics
        await verifyAndFetch();
        showMessage('Card uploaded successfully!');
      }
    } catch (err) {
      showMessage('Failed to upload card', 'error');
    } finally {
      setUploading({ ...uploading, [topicId]: false });
    }
  };

  const addVideoUrl = async (topicId, videoUrl, videoTitle) => {
    if (!videoUrl.trim()) {
      showMessage('Video URL is required', 'error');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/topics/${topicId}/videos?api_key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            url: videoUrl,
            title: videoTitle || 'Walkthrough Video'
          })
        }
      );
      
      if (response.ok) {
        await verifyAndFetch();
        showMessage('Video added successfully!');
      }
    } catch (err) {
      showMessage('Failed to add video', 'error');
    }
  };

  const removeCard = async (topicId, cardIndex) => {
    if (!window.confirm('Remove this card?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/topics/${topicId}/cards/${cardIndex}?api_key=${apiKey}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        await verifyAndFetch();
        showMessage('Card removed');
      }
    } catch (err) {
      showMessage('Failed to remove card', 'error');
    }
  };

  const toggleTopicActive = async (topicId, isActive) => {
    await updateTopic(topicId, { is_active: !isActive });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (!authenticated || showApiKeyInput) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-2 text-center">
            Admin Access
          </h1>
          <p className="text-[#64748B] text-center mb-6">Enter admin API key to manage the Topic Store</p>
          
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Admin API Key"
            className="w-full h-12 px-4 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] mb-4"
          />
          
          <Button 
            onClick={handleLogin}
            className="w-full h-12 bg-[#0F172A] hover:bg-[#1E293B] text-white"
          >
            Access Admin Panel
          </Button>
          
          <Link to="/" className="block text-center text-[#64748B] text-sm mt-4 hover:text-[#C5A059]">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Message Toast */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          message.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {message.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Header */}
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Site</span>
          </Link>
          <h1 className="font-['Playfair_Display'] text-xl font-bold">
            Topic Store Admin
          </h1>
          <Link to="/store" className="text-[#C5A059] hover:underline text-sm">
            View Store →
          </Link>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#0F172A]">{topics.length}</p>
                <p className="text-sm text-[#64748B]">Total Topics</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {topics.filter(t => t.is_active).length}
                </p>
                <p className="text-sm text-[#64748B]">Active</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#C5A059]">
                  {topics.reduce((acc, t) => acc + (t.cards?.length || 0), 0)}
                </p>
                <p className="text-sm text-[#64748B]">Total Cards</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-500">
                  {topics.filter(t => t.flash_sale?.enabled).length}
                </p>
                <p className="text-sm text-[#64748B]">Flash Sales</p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowNewTopicForm(true)}
              className="bg-[#C5A059] hover:bg-[#B8934E] text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add New Topic
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* New Topic Form */}
        {showNewTopicForm && (
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#0F172A]">Create New Topic</h2>
              <button onClick={() => setShowNewTopicForm(false)} className="text-[#64748B] hover:text-[#0F172A]">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1">Topic Name *</label>
                <input
                  type="text"
                  value={newTopic.topic_name}
                  onChange={(e) => setNewTopic({ ...newTopic, topic_name: e.target.value })}
                  placeholder="e.g., Solving Quadratic Equations"
                  className="w-full h-11 px-4 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1">Grade</label>
                  <select
                    value={newTopic.grade}
                    onChange={(e) => setNewTopic({ ...newTopic, grade: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059]"
                  >
                    <option>Grade 9</option>
                    <option>Grade 8</option>
                    <option>Grade 7</option>
                    <option>Grade 6</option>
                    <option>Grade 5</option>
                    <option>Grade 4</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1">Tier</label>
                  <select
                    value={newTopic.tier}
                    onChange={(e) => setNewTopic({ ...newTopic, tier: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059]"
                  >
                    <option>Higher</option>
                    <option>Foundation</option>
                    <option>Both</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Description (optional)</label>
              <textarea
                value={newTopic.description}
                onChange={(e) => setNewTopic({ ...newTopic, description: e.target.value })}
                placeholder="Brief description of what this topic covers..."
                rows={2}
                className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]"
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowNewTopicForm(false)}>
                Cancel
              </Button>
              <Button onClick={createTopic} className="bg-[#C5A059] hover:bg-[#B8934E] text-white">
                <Save className="w-4 h-4 mr-2" />
                Create Topic
              </Button>
            </div>
          </div>
        )}

        {/* Topics List */}
        <div className="space-y-4">
          {topics.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center">
              <GraduationCap className="w-16 h-16 text-[#C5A059]/40 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2">No Topics Yet</h3>
              <p className="text-[#64748B] mb-4">Create your first topic to start adding revision cards</p>
              <Button
                onClick={() => setShowNewTopicForm(true)}
                className="bg-[#C5A059] hover:bg-[#B8934E] text-white"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create First Topic
              </Button>
            </div>
          ) : (
            topics.map(topic => (
              <TopicAdminCard
                key={topic.topic_id}
                topic={topic}
                expanded={expandedTopics[topic.topic_id]}
                onToggleExpand={() => setExpandedTopics({
                  ...expandedTopics,
                  [topic.topic_id]: !expandedTopics[topic.topic_id]
                })}
                onToggleActive={() => toggleTopicActive(topic.topic_id, topic.is_active)}
                onUploadCard={(file) => uploadCardImage(topic.topic_id, file)}
                onAddVideo={(url, title) => addVideoUrl(topic.topic_id, url, title)}
                onRemoveCard={(idx) => removeCard(topic.topic_id, idx)}
                onSetFlashSale={(saleData) => setFlashSale(topic.topic_id, saleData)}
                uploading={uploading[topic.topic_id]}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ============ TOPIC ADMIN CARD COMPONENT ============
function TopicAdminCard({ 
  topic, 
  expanded, 
  onToggleExpand, 
  onToggleActive,
  onUploadCard, 
  onAddVideo,
  onRemoveCard,
  onSetFlashSale,
  uploading 
}) {
  const fileInputRef = useRef(null);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showSaleForm, setShowSaleForm] = useState(false);
  const [saleDiscount, setSaleDiscount] = useState(30);
  const [saleLabel, setSaleLabel] = useState('Flash Sale!');
  const [saleEndDate, setSaleEndDate] = useState('');
  const [saleEndTime, setSaleEndTime] = useState('');

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => onUploadCard(file));
    e.target.value = '';
  };

  const handleAddVideo = () => {
    onAddVideo(newVideoUrl, newVideoTitle);
    setNewVideoUrl('');
    setNewVideoTitle('');
    setShowVideoForm(false);
  };

  const handleEnableSale = () => {
    const saleEnd = saleEndDate && saleEndTime 
      ? new Date(`${saleEndDate}T${saleEndTime}`).toISOString()
      : null;
    
    onSetFlashSale({
      enabled: true,
      discount_percent: saleDiscount,
      sale_label: saleLabel,
      sale_end: saleEnd
    });
    setShowSaleForm(false);
  };

  const handleDisableSale = () => {
    onSetFlashSale({ enabled: false });
  };

  const flashSale = topic.flash_sale || {};

  return (
    <div className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
      flashSale.enabled ? 'border-red-300 ring-2 ring-red-100' : topic.is_active ? 'border-[#E2E8F0]' : 'border-red-200 bg-red-50/30'
    }`}>
      {/* Flash Sale Banner */}
      {flashSale.enabled && (
        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Tag className="w-4 h-4" />
            <span className="font-bold">{flashSale.sale_label}</span>
            <span className="bg-white/20 px-2 py-0.5 rounded text-sm">
              {flashSale.discount_percent}% OFF
            </span>
          </div>
          {flashSale.sale_end && (
            <span className="text-white/80 text-sm flex items-center gap-1">
              <Timer className="w-4 h-4" />
              Ends: {new Date(flashSale.sale_end).toLocaleDateString()}
            </span>
          )}
        </div>
      )}
      
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#F8FAFC]"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            flashSale.enabled ? 'bg-red-100' : topic.is_active ? 'bg-[#C5A059]/10' : 'bg-red-100'
          }`}>
            <BookOpen className={`w-6 h-6 ${flashSale.enabled ? 'text-red-500' : topic.is_active ? 'text-[#C5A059]' : 'text-red-400'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-[#0F172A]">{topic.topic_name}</h3>
            <div className="flex items-center gap-2 text-sm text-[#64748B]">
              <span>{topic.grade}</span>
              <span>•</span>
              <span>{topic.tier}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {topic.cards?.length || 0} cards
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Play className="w-3 h-3" />
                {topic.videos?.length || 0} videos
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleActive(); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
              topic.is_active 
                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            {topic.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {topic.is_active ? 'Active' : 'Hidden'}
          </button>
          {expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-[#E2E8F0] p-4">
          {/* Description */}
          {topic.description && (
            <p className="text-[#64748B] text-sm mb-4 p-3 bg-[#F8FAFC] rounded-lg">
              {topic.description}
            </p>
          )}

          {/* Cards Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-[#0F172A] flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-[#C5A059]" />
                Revision Cards
              </h4>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload Cards
                </Button>
              </div>
            </div>

            {topic.cards?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {topic.cards.map((card, idx) => (
                  <div key={idx} className="relative group">
                    <div className="aspect-[3/4] rounded-lg overflow-hidden border border-[#E2E8F0]">
                      <img
                        src={card.url || card}
                        alt={card.title || `Card ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => onRemoveCard(idx)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-xs text-[#64748B] mt-1 truncate">
                      {card.title || `Card ${idx + 1}`}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-8 text-center">
                <ImageIcon className="w-10 h-10 text-[#C5A059]/40 mx-auto mb-2" />
                <p className="text-[#64748B] text-sm">No cards yet. Upload your first revision card!</p>
              </div>
            )}
          </div>

          {/* Videos Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-[#0F172A] flex items-center gap-2">
                <Play className="w-4 h-4 text-[#C5A059]" />
                Videos
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowVideoForm(!showVideoForm)}
                className="border-[#E2E8F0]"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Add Video URL
              </Button>
            </div>

            {showVideoForm && (
              <div className="bg-[#F8FAFC] rounded-lg p-4 mb-4">
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <input
                      type="url"
                      value={newVideoUrl}
                      onChange={(e) => setNewVideoUrl(e.target.value)}
                      placeholder="YouTube URL (e.g., https://youtube.com/watch?v=...)"
                      className="w-full h-10 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newVideoTitle}
                      onChange={(e) => setNewVideoTitle(e.target.value)}
                      placeholder="Video title"
                      className="flex-1 h-10 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] text-sm"
                    />
                    <Button
                      size="sm"
                      onClick={handleAddVideo}
                      disabled={!newVideoUrl.trim()}
                      className="bg-[#C5A059] hover:bg-[#B8934E] text-white h-10"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {topic.videos?.length > 0 ? (
              <div className="space-y-2">
                {topic.videos.map((video, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-[#F8FAFC] rounded-lg">
                    <Play className="w-5 h-5 text-[#C5A059]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0F172A] text-sm truncate">
                        {video.title || `Video ${idx + 1}`}
                      </p>
                      <p className="text-xs text-[#64748B] truncate">{video.url}</p>
                    </div>
                    <a
                      href={video.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#C5A059] hover:underline text-sm"
                    >
                      View →
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#64748B] text-sm">No videos added yet.</p>
            )}
          </div>

          {/* Flash Sale Section */}
          <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-[#0F172A] flex items-center gap-2">
                <Tag className="w-4 h-4 text-red-500" />
                Flash Sale
              </h4>
              {flashSale.enabled ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDisableSale}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  End Sale
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setShowSaleForm(!showSaleForm)}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Start Flash Sale
                </Button>
              )}
            </div>

            {showSaleForm && !flashSale.enabled && (
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">
                      Discount %
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="5"
                        max="90"
                        step="5"
                        value={saleDiscount}
                        onChange={(e) => setSaleDiscount(parseInt(e.target.value))}
                        className="flex-1"
                      />
                      <span className="text-lg font-bold text-red-500 w-12">
                        {saleDiscount}%
                      </span>
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">
                      Sale price: £{((0.99 * (100 - saleDiscount)) / 100).toFixed(2)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">
                      Sale Label
                    </label>
                    <select
                      value={saleLabel}
                      onChange={(e) => setSaleLabel(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-[#E2E8F0] focus:border-red-400"
                    >
                      <option>Flash Sale!</option>
                      <option>New Topic Special</option>
                      <option>Limited Offer</option>
                      <option>Back to School Sale</option>
                      <option>Weekend Deal</option>
                      <option>Exam Season Special</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">
                      End Date (optional)
                    </label>
                    <input
                      type="date"
                      value={saleEndDate}
                      onChange={(e) => setSaleEndDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full h-10 px-3 rounded-lg border border-[#E2E8F0] focus:border-red-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">
                      End Time (optional)
                    </label>
                    <input
                      type="time"
                      value={saleEndTime}
                      onChange={(e) => setSaleEndTime(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-[#E2E8F0] focus:border-red-400"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowSaleForm(false)}
                    className="border-[#E2E8F0]"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleEnableSale}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Enable Flash Sale
                  </Button>
                </div>
              </div>
            )}

            {flashSale.enabled && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-green-700 text-sm">
                  <Check className="w-4 h-4 inline mr-1" />
                  Flash sale is active! Customers will see <strong>{flashSale.discount_percent}% off</strong> with the label "{flashSale.sale_label}".
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
